import {computed, type Ref} from "vue";
import {useQuery, useMutation, useQueryClient, keepPreviousData} from "@tanstack/vue-query";
import {fetchWrapper} from "@/helpers/fetch-wrapper";
import type {ApiError} from "@/helpers/api-error";
import type {Workflow, RunHistory, Service, WizardDefinition, ConnectionSlot} from "@/types";
import type {Paged} from "@/queries/projects";
import {
    serverFilterParamsToQueryString,
    type ServerFilterParams,
} from "@/views/builder/composables/useHistoryFilters";

const BASE = `${import.meta.env.VITE_API_URL}/workflows`;

// READ — single workflow (optionally replayed at a specific run)
export function useWorkflow(id: Ref<string>, runId?: Ref<string | null>) {
    const queryKey = computed(() => ["workflow", id.value, runId?.value ?? null]);

    const {data: workflow, isFetching: workflowLoading, error: workflowError} = useQuery({
        queryKey,
        queryFn: () => {
            const uri = runId?.value ? `${BASE}/${id.value}?workflowRunId=${runId.value}` : `${BASE}/${id.value}`;
            return fetchWrapper.get<Workflow>(uri);
        },
        enabled: computed(() => !!id.value),
    });

    return {workflow, workflowLoading, workflowError: workflowError as Ref<ApiError | null>};
}

// READ — all workflows (flat list)
export function useWorkflows() {
    const {data, isFetching: workflowsLoading, error: workflowsError} = useQuery({
        queryKey: ["workflows"],
        queryFn: () => fetchWrapper.get<Workflow[]>(`${BASE}/`),
    });
    const workflows = computed(() => data.value ?? []);

    return {workflows, workflowsLoading, workflowsError: workflowsError as Ref<ApiError | null>};
}

// READ — one offset page of a workflow's run history AS EXECUTED UNDER a given project. Scoped to
// the project (RunningProjectId) as well as the workflow, so a shared/master workflow that ran under
// other projects doesn't leak its foreign runs into this project's builder. This goes through the
// project-runs endpoint (GET /projects/{id}/runs?workflowId=…) — the same correctly-scoped path the
// project History tab uses — rather than /workflows/{id}/runs, which scopes by workflow alone.
// Optional filterParams ref drives server-side filtering, sorting, and paging — see useProjectRuns.
export function useWorkflowRuns(
    projectId: Ref<string>,
    workflowId: Ref<string>,
    filterParams?: Ref<ServerFilterParams>,
) {
    // The builder always narrows to its own workflow; the History tab's workflow dropdown doesn't
    // apply here, so force workflowId rather than reading it from filterParams.
    const queryString = computed(() => {
        const base = filterParams
            ? { ...filterParams.value, workflowId: workflowId.value }
            : ({ workflowId: workflowId.value } as ServerFilterParams);
        return serverFilterParamsToQueryString(base as ServerFilterParams);
    });
    const {data, isLoading: workflowRunsLoading, isFetching: workflowRunsFetching, error: workflowRunsError} = useQuery({
        queryKey: computed(() => ["workflow-runs", projectId.value, workflowId.value, queryString.value]),
        queryFn: () => {
            const qs = queryString.value ? `?${queryString.value}` : "";
            return fetchWrapper.get<Paged<RunHistory>>(
                `${import.meta.env.VITE_API_URL}/projects/${projectId.value}/runs${qs}`,
            );
        },
        enabled: computed(() => !!projectId.value && !!workflowId.value),
        placeholderData: keepPreviousData,
    });
    const workflowRuns = computed(() => data.value?.items ?? []);
    const workflowRunsTotal = computed(() => data.value?.totalCount ?? 0);

    return {
        workflowRuns,
        workflowRunsTotal,
        workflowRunsLoading,
        workflowRunsFetching,
        workflowRunsError: workflowRunsError as Ref<ApiError | null>,
    };
}

// WRITE — create
export function useCreateWorkflow() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        string,
        ApiError,
        { projectId: string; name: string }
    >({
        mutationFn: (payload) => fetchWrapper.post<string>(`${BASE}/`, payload),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflows"]}),
    });

    return {
        createWorkflow: mutate,
        createWorkflowAsync: mutateAsync,
        createWorkflowPending: isPending,
        createWorkflowError: error as Ref<ApiError | null>,
        createWorkflowReset: reset,
    };
}

// The workflow export bundle — a portable snapshot of a workflow's editable content. The frontend
// treats it as an opaque payload it downloads and re-uploads; only the envelope fields are inspected
// (to validate a dropped file before sending it back). `services` are domain snapshots, deliberately
// not modelled here — the backend owns their shape.
export interface WorkflowExportBundle {
    schemaVersion: string;
    kind: string;
    name: string;
    services: unknown[];
    rootServiceId?: string | null;
    wizardDefinitions: unknown[];
    connectionSlots: unknown[];
    exportedAt: string;
}

export interface WorkflowImportOutcome {
    workflow: Workflow;
    validationWarnings: string[];
}

/** The kind tag every workflow bundle carries — checked before import to reject unrelated JSON. */
export const WORKFLOW_BUNDLE_KIND = "blackbox.workflow";

// READ — fetch a workflow's export bundle. Not a cached query: it's an on-demand action (the user
// clicked Export), so it's a plain fetch the caller downloads.
export function fetchWorkflowExport(workflowId: string) {
    return fetchWrapper.get<WorkflowExportBundle>(`${BASE}/${workflowId}/export`);
}

// WRITE — replace the current workflow's working copy with an imported bundle. Lands as Draft;
// returns the reloaded workflow plus any expression-validation warnings to surface. Invalidates the
// workflow query so the builder canvas reloads with the imported services.
export function useImportWorkflow(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        WorkflowImportOutcome,
        ApiError,
        WorkflowExportBundle
    >({
        mutationFn: (bundle) =>
            fetchWrapper.post<WorkflowImportOutcome>(`${BASE}/${workflowId.value}/import`, bundle),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        importWorkflow: mutate,
        importWorkflowAsync: mutateAsync,
        importWorkflowPending: isPending,
        importWorkflowError: error as Ref<ApiError | null>,
        importWorkflowReset: reset,
    };
}

// WRITE — save wizard definition for a workflow
export function useSaveWizardDefinition(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        Omit<WizardDefinition, "id" | "configuration">
    >({
        mutationFn: (definition) =>
            fetchWrapper.put(`${BASE}/${workflowId.value}/wizard`, definition),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        saveWizardDefinition: mutate,
        saveWizardDefinitionAsync: mutateAsync,
        saveWizardDefinitionPending: isPending,
        saveWizardDefinitionError: error as Ref<ApiError | null>,
        saveWizardDefinitionReset: reset,
    };
}

// WRITE — save workflow settings
export function useSaveWorkflow() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        Workflow
    >({
        mutationFn: (workflow: Workflow) => fetchWrapper.put(`${BASE}/${workflow.id}`, workflow),
        onSuccess: (_data, workflow) => queryClient.invalidateQueries({queryKey: ["workflow", workflow.id]}),
    });

    return {
        saveWorkflow: mutate,
        saveWorkflowAsync: mutateAsync,
        saveWorkflowPending: isPending,
        saveWorkflowError: error as Ref<ApiError | null>,
        saveWorkflowReset: reset,
    };
}

// WRITE — add service node
export function useAddService(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        string,
        ApiError,
        { name: string; serviceType: any; parentServiceIds: string[] }
    >({
        mutationFn: (payload) => fetchWrapper.post<string>(`${BASE}/${workflowId.value}/service/add`, payload),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        addService: mutate,
        addServiceAsync: mutateAsync,
        addServicePending: isPending,
        addServiceError: error as Ref<ApiError | null>,
        addServiceReset: reset,
    };
}

// WRITE — duplicate a service node. Deep-copies the source service server-side and returns the new
// service id; the config (model, filter, parse settings, event handlers, connection) is copied too.
export function useDuplicateService(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        string,
        ApiError,
        string
    >({
        mutationFn: (serviceId: string) =>
            fetchWrapper.post<string>(`${BASE}/${workflowId.value}/service/duplicate/${serviceId}`),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        duplicateService: mutate,
        duplicateServiceAsync: mutateAsync,
        duplicateServicePending: isPending,
        duplicateServiceError: error as Ref<ApiError | null>,
        duplicateServiceReset: reset,
    };
}

// WRITE — update service node
export function useUpdateService(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    // `service` is a partial update — the API's UpdateServiceDto has every field optional, so a
    // name-only rename sends just `{ name }`. Only the provided fields are applied server-side.
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        { serviceId: string; service: Partial<Service> }
    >({
        mutationFn: ({serviceId, service}) =>
            fetchWrapper.put(`${BASE}/${workflowId.value}/service/update/${serviceId}`, service),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        updateService: mutate,
        updateServiceAsync: mutateAsync,
        updateServicePending: isPending,
        updateServiceError: error as Ref<ApiError | null>,
        updateServiceReset: reset,
    };
}

// WRITE — define a typed connection slot on the workflow (ADR-030). Idempotent by name; a same-name
// slot of a different template is rejected by the API. Slots are immutable — no update/rename.
export function useCreateConnectionSlot(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        ConnectionSlot,
        ApiError,
        { name: string; connectionTemplateId: string }
    >({
        mutationFn: (payload) =>
            fetchWrapper.post<ConnectionSlot>(`${BASE}/${workflowId.value}/connection-slots`, payload),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        createConnectionSlot: mutate,
        createConnectionSlotAsync: mutateAsync,
        createConnectionSlotPending: isPending,
        createConnectionSlotError: error as Ref<ApiError | null>,
        createConnectionSlotReset: reset,
    };
}

// WRITE — delete a connection slot by name (ADR-030). The API rejects deletion while a service
// `connectionName` or a `{{ connection.<name> }}` expression still references it.
export function useDeleteConnectionSlot(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (slotName: string) =>
            fetchWrapper.delete(`${BASE}/${workflowId.value}/connection-slots/${encodeURIComponent(slotName)}`),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        deleteConnectionSlot: mutate,
        deleteConnectionSlotAsync: mutateAsync,
        deleteConnectionSlotPending: isPending,
        deleteConnectionSlotError: error as Ref<ApiError | null>,
        deleteConnectionSlotReset: reset,
    };
}

// WRITE — Phase 4: toggle whether a service appears in the product's Components library.
export function useSetServiceIsComponent(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<
        unknown,
        ApiError,
        { serviceId: string; isComponent: boolean }
    >({
        mutationFn: ({ serviceId, isComponent }) =>
            fetchWrapper.put(
                `${BASE}/${workflowId.value}/service/${serviceId}/is-component`,
                { isComponent }
            ),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workflow", workflowId.value] }),
    });

    return {
        setServiceIsComponent: mutate,
        setServiceIsComponentAsync: mutateAsync,
        setServiceIsComponentPending: isPending,
        setServiceIsComponentError: error as Ref<ApiError | null>,
        setServiceIsComponentReset: reset,
    };
}

// WRITE — delete service node
export function useDeleteService(workflowId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (serviceId: string) =>
            fetchWrapper.post(`${BASE}/${workflowId.value}/service/remove/${serviceId}`),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["workflow", workflowId.value]}),
    });

    return {
        deleteService: mutate,
        deleteServiceAsync: mutateAsync,
        deleteServicePending: isPending,
        deleteServiceError: error as Ref<ApiError | null>,
        deleteServiceReset: reset,
    };
}

// READ — the {{ input.* }} keys a workflow references. Powers the Run modal / Saved Call inputs form.
// Route is project-scoped for URL consistency with the other workflow endpoints; the keys are a
// property of the workflow definition itself, discovered from its expressions.
export interface WorkflowInputKeys {
    workflowId: string;
    keys: string[];
}

export function useWorkflowInputKeys(projectId: Ref<string>, workflowId: Ref<string | null>) {
    const queryKey = computed(() => ["workflow-input-keys", workflowId.value]);

    const { data: inputKeys, isFetching: inputKeysLoading, error: inputKeysError } = useQuery({
        queryKey,
        queryFn: () =>
            fetchWrapper.get<WorkflowInputKeys>(
                `${import.meta.env.VITE_API_URL}/projects/${projectId.value}/workflows/${workflowId.value}/input-keys`
            ),
        enabled: computed(() => !!projectId.value && !!workflowId.value),
    });

    return { inputKeys, inputKeysLoading, inputKeysError: inputKeysError as Ref<ApiError | null> };
}

// WRITE — start a workflow run. POSTs to the first-class workflow-runs resource. `projectId` is the
// run's owning project; `projectContextId` optionally overrides which project's configuration drives
// the run context (builder preview against another of the org's projects). `inputs` are the per-run
// {{ input.* }} values collected by the Run modal / Saved Call.
export function useStartWorkflowRun() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation<
        string,
        ApiError,
        {
            workflowId: string;
            projectId: string;
            projectContextId?: string;
            overrideReadiness?: boolean;
            inputs?: Record<string, unknown>;
        }
    >({
        // overrideReadiness defaults false = safe-by-default (ADR-033): a run with blocking config
        // gaps returns 422; the pre-run confirm resubmits with true to run anyway.
        mutationFn: ({ workflowId, projectId, projectContextId, overrideReadiness, inputs }) =>
            fetchWrapper.post<string>(
                `${import.meta.env.VITE_API_URL}/workflow-runs`,
                { workflowId, projectId, projectContextId, overrideReadiness, inputs }
            ),
        // Key is ["workflow-runs", projectId, workflowId, …]; invalidate by the (project, workflow)
        // prefix so the project-scoped builder list refetches after a run starts.
        onSuccess: (_runId, vars) =>
            queryClient.invalidateQueries({ queryKey: ["workflow-runs", vars.projectId, vars.workflowId] }),
    });

    return {
        startWorkflowRun: mutate,
        startWorkflowRunAsync: mutateAsync,
        startWorkflowRunPending: isPending,
        startWorkflowRunError: error as Ref<ApiError | null>,
        startWorkflowRunReset: reset,
    };
}

// WRITE — publish a workflow version
export function usePublishWorkflow() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        Workflow,
        ApiError,
        string
    >({
        mutationFn: (workflowId: string) =>
            fetchWrapper.post<Workflow>(`${BASE}/${workflowId}/publish`),
        onSuccess: (_data, workflowId) => {
            queryClient.invalidateQueries({queryKey: ['workflow', workflowId]});
            queryClient.invalidateQueries({queryKey: ['project-workflows']});
            // Publishing creates a new WorkflowVersion — refresh the dropdown's version list.
            queryClient.invalidateQueries({queryKey: ['workflow-versions', workflowId]});
        },
    });

    return {
        publishWorkflow: mutate,
        publishWorkflowAsync: mutateAsync,
        publishWorkflowPending: isPending,
        publishWorkflowError: error as Ref<ApiError | null>,
        publishWorkflowReset: reset,
    };
}

export interface WorkflowVersionSummary {
    id: string;
    versionLabel: string;
    publishedAt: string;
}

// READ — published versions of a workflow, newest first (drives the version dropdown + Restore)
export function useWorkflowVersions(workflowId: Ref<string>) {
    const {data, isLoading, isFetching, error} = useQuery({
        queryKey: computed(() => ['workflow-versions', workflowId.value]),
        queryFn: () =>
            fetchWrapper.get<WorkflowVersionSummary[]>(`${BASE}/${workflowId.value}/versions`),
        enabled: computed(() => !!workflowId.value),
    });

    return {
        workflowVersions: computed(() => data.value ?? []),
        workflowVersionsLoading: isLoading,
        workflowVersionsFetching: isFetching,
        workflowVersionsError: error as Ref<ApiError | null>,
    };
}

// WRITE — restore an older published version's content into the working copy as new edits
export function useRestoreWorkflowVersion() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        Workflow,
        ApiError,
        {workflowId: string; versionId: string}
    >({
        mutationFn: ({workflowId, versionId}) =>
            fetchWrapper.post<Workflow>(`${BASE}/${workflowId}/restore/${versionId}`),
        onSuccess: (_data, {workflowId}) => {
            queryClient.invalidateQueries({queryKey: ['workflow', workflowId]});
            queryClient.invalidateQueries({queryKey: ['project-workflows']});
        },
    });

    return {
        restoreWorkflowVersion: mutate,
        restoreWorkflowVersionAsync: mutateAsync,
        restoreWorkflowVersionPending: isPending,
        restoreWorkflowVersionError: error as Ref<ApiError | null>,
        restoreWorkflowVersionReset: reset,
    };
}

// WRITE — discard unpublished edits, reverting the working copy to its last published version
export function useDiscardWorkflowChanges() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        Workflow,
        ApiError,
        string
    >({
        mutationFn: (workflowId: string) =>
            fetchWrapper.post<Workflow>(`${BASE}/${workflowId}/discard`),
        onSuccess: (_data, workflowId) => {
            queryClient.invalidateQueries({queryKey: ['workflow', workflowId]});
            queryClient.invalidateQueries({queryKey: ['project-workflows']});
        },
    });

    return {
        discardWorkflowChanges: mutate,
        discardWorkflowChangesAsync: mutateAsync,
        discardWorkflowChangesPending: isPending,
        discardWorkflowChangesError: error as Ref<ApiError | null>,
        discardWorkflowChangesReset: reset,
    };
}
